import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import PeriodSelect from "./PeriodSelect";
import WidgetShell from "./WidgetShell";
import { ENTERPRISE_CHART_COLORS } from "./chartTheme";
import { ExdGhostButton } from "./ExdControls";
import { ExdEmpty, ExdRankRow, scrollbarSx } from "./exdMui";

const extractRows = (chartOptions) => {
  const raw = chartOptions?.series?.[0]?.data;
  if (!Array.isArray(raw) || !raw.length) return [];

  return raw
    .map((point, index) => {
      if (point && typeof point === "object" && !Array.isArray(point)) {
        return {
          name: point.name || `Item ${index + 1}`,
          value: Number(point.y) || 0,
          list: Array.isArray(point.list) ? point.list : null,
        };
      }
      return {
        name: `Item ${index + 1}`,
        value: Number(point) || 0,
        list: null,
      };
    })
    .filter((row) => Number(row.value) > 0)
    .sort((a, b) => b.value - a.value);
};

const RankListWidget = ({
  title,
  infoTitle,
  loading,
  chartOptions,
  periodValue,
  onPeriodChange,
  periodFallback = "All",
  actionLabel,
  onAction,
}) => {
  const rows = useMemo(() => extractRows(chartOptions), [chartOptions]);

  const total = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0),
    [rows],
  );

  const maxValue = useMemo(
    () => Math.max(...rows.map((row) => Number(row.value) || 0), 0),
    [rows],
  );

  const renderTooltip = (row, pct) => {
    const hasList = Array.isArray(row.list) && row.list.length > 0;

    return (
      <Typography component="div" sx={{ fontSize: 13, maxWidth: 280 }}>
        <strong>{row.name}</strong>
        <Box>
          {nFormatter(row.value, 2)} · {pct.toFixed(1)}%
        </Box>
        {hasList ? (
          <Box sx={{ mt: 1, maxHeight: 220, overflowY: "auto", pr: 0.5, ...scrollbarSx }}>
            {row.list.map((item, i) => (
              <Box
                key={`${item.itemCode || item.itemName}-${i}`}
                sx={{ mb: 0.5 }}
              >
                <strong>{item.itemName || item.name}</strong>
                {" · "}
                {nFormatter(Number(item.usedCount ?? item.y ?? item.value) || 0, 2)}
              </Box>
            ))}
          </Box>
        ) : null}
      </Typography>
    );
  };

  return (
    <WidgetShell
      title={title}
      infoTitle={infoTitle}
      loading={loading}
      controls={
        <>
          {actionLabel && onAction ? (
            <ExdGhostButton onClick={onAction}>{actionLabel}</ExdGhostButton>
          ) : null}
          {onPeriodChange ? (
            <PeriodSelect
              value={periodValue}
              onChange={onPeriodChange}
              fallbackLabel={periodFallback}
            />
          ) : null}
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 1.125,
          height: 340,
          maxHeight: 340,
          minHeight: 340,
          overflowX: "hidden",
          overflowY: "auto",
          pr: 0.5,
          width: "100%",
          ...scrollbarSx,
        }}
      >
        {rows.length ? (
          rows.map((row, index) => {
            const pct = total ? (row.value / total) * 100 : 0;
            const barPct = maxValue ? (row.value / maxValue) * 100 : 0;
            const color =
              ENTERPRISE_CHART_COLORS[index % ENTERPRISE_CHART_COLORS.length];
            const hasList = Array.isArray(row.list) && row.list.length > 0;

            return (
              <Tooltip
                key={`${row.name}-${index}`}
                title={renderTooltip(row, pct)}
                placement="left"
                enterDelay={150}
                leaveDelay={80}
                disableInteractive={!hasList}
              >
                <Box>
                  <ExdRankRow
                    label={row.name}
                    value={`${pct.toFixed(1)}%`}
                    barPct={barPct}
                    color={color}
                    extra={hasList ? `· ${row.list.length} items` : null}
                  />
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <ExdEmpty />
        )}
      </Box>
    </WidgetShell>
  );
};

export default RankListWidget;
